import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@reverbdotcom/commons/src/gql';
import I18n from 'i18n-js';
import React, { useEffect } from 'react';

import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { core_apimessages_Image, core_apimessages_ImageEntry, DataServicesSellFormPricingRecommendations } from '@reverbdotcom/commons/src/gql/graphql';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatAmount } from '@reverbdotcom/commons/src/money';
import { RCLoadingBars, RCText } from '@reverbdotcom/cadence/components';
import { convertMoney } from '@reverbdotcom/commons/src/convert_currency';
import { PriceDisplay } from '@reverbdotcom/commons/src/components/PriceDisplay';
import PriceDisplayRange from '@reverbdotcom/commons/src/components/price_display_range';
import { conditionUuidForSlug, NON_FUNCTIONING, BRAND_NEW, B_STOCK } from '@reverbdotcom/commons/src/condition_helpers';

import PriceGuideGraphIcon from '../../../../../../images/price-guide-tool/colored-price-guide-graph.svg';
import SellFormPricingGuidanceModal from './SellFormPricingGuidanceModal';
import eppoFlags from '@reverbdotcom/commons/src/eppoFlags';
import { useEppoExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import classNames from 'classnames';
import { CloudinaryPhoto } from '@reverbdotcom/commons/src/components/PhotoUploader/CloudinaryPhotoUploader/cloudinaryPhoto';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';

interface IProps {
  conditionSlug: string;
  listingImage?: core_apimessages_Image | core_apimessages_ImageEntry;
  canonicalProduct?: {
    title: string;
    id: string;
  };
  isValidShopLocation: boolean;
  countryCode: string;
  updatePriceRecommendation: React.Dispatch<any>;
  shopCurrency: string;
}

export function convertPriceRecommendation({
  priceRecommendation,
  currency,
  exchangeRates,
}) {
  const { priceLow, priceMiddle, priceHigh } = priceRecommendation ?? {};

  if (!priceLow || !priceMiddle || !priceHigh) return {};

  let exchangedLow;
  let exchangedMiddle;
  let exchangedHigh;

  try {
    exchangedLow = convertMoney(priceLow, currency, exchangeRates);
    exchangedMiddle = convertMoney(priceMiddle, currency, exchangeRates);
    exchangedHigh = convertMoney(priceHigh, currency, exchangeRates);
  } catch {
    return {};
  }

  const displayLow = formatAmount(exchangedLow, { precision: 0 });
  const displayMiddle = formatAmount(exchangedMiddle, { precision: 0 });
  const displayHigh = formatAmount(exchangedHigh, { precision: 0 });

  const formattedPriceLow = {
    amountCents: exchangedLow.amountCents,
    amount: exchangedLow.amount,
    currency: exchangedLow.currency,
    display: displayLow,
  };

  const formattedPriceMiddle = {
    amountCents: exchangedMiddle.amountCents,
    amount: exchangedMiddle.amount,
    currency: exchangedMiddle.currency,
    display: displayMiddle,
  };

  const formattedPriceHigh = {
    amountCents: exchangedHigh.amountCents,
    amount: exchangedHigh.amount,
    currency: exchangedHigh.currency,
    display: displayHigh,
  };

  return {
    priceLow: formattedPriceLow,
    priceMiddle: formattedPriceMiddle,
    priceHigh: formattedPriceHigh,
  };
}

export function isConditionIneligibleForEstimates(conditionSlug) {
  return !conditionSlug || [BRAND_NEW, NON_FUNCTIONING, B_STOCK].includes(conditionSlug);
}

function shouldSkipQuery({
  canonicalProduct,
  conditionSlug,
  isValidShopLocation,
}) {
  return !canonicalProduct?.id || !isValidShopLocation || isConditionIneligibleForEstimates(conditionSlug);
}

export default function SellFormPricingGuidance({
  canonicalProduct,
  conditionSlug,
  listingImage,
  updatePriceRecommendation,
  isValidShopLocation,
  countryCode,
  shopCurrency,
}: ChildProps<IProps, DataServicesSellFormPricingRecommendations.Query>) {
  const user = useUser();
  const canonicalProductId = canonicalProduct.id;
  const conditionUuids = [conditionUuidForSlug(conditionSlug)];
  const priceRecommendationQueries = conditionUuids.filter(Boolean).map(conditionUuid => ({
    canonicalProductId,
    conditionUuid,
    countryCode,
    currency: shopCurrency,
  }));
  const { data, loading } = useQuery(SELL_FORM_PRICE_RECOMMENDATIONS_QUERY, {
    ssr: false,
    fetchPolicy: 'network-only',
    skip: shouldSkipQuery({
      canonicalProduct,
      conditionSlug,
      isValidShopLocation,
    }),
    variables: {
      loggedOut: user.loggedOut,
      priceRecommendationQueries,
    },
  });

  const {
    me,
    exchangeRates,
    priceRecommendations,
  } = data ?? {};

  const rates = exchangeRates?.rates ?? [];
  const recommendations = priceRecommendations?.priceRecommendations ?? [];
  const currency = me?.shop?.currency || user.currency;

  const priceRecommendation = convertPriceRecommendation({
    priceRecommendation: recommendations[0],
    exchangeRates: rates,
    currency,
  });

  const { priceLow, priceMiddle, priceHigh } = priceRecommendation;

  const hasNoEstimate = !priceLow || !priceMiddle || !priceHigh;
  const hasDataServicesPriceEstimate = !hasNoEstimate;
  const autofillPrice = useEppoExpEnabled({ flagKey: eppoFlags.SELL_FORM_AUTOFILL_PRICE });
  const photoSrc = autofillPrice && listingImage ? new CloudinaryPhoto(listingImage).thumbUrl : PriceGuideGraphIcon;

  useEffect(() => {
    if (!loading) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: 'SellFormPricingGuidance',
        hasDataServicesPriceEstimate,
        cpId: canonicalProduct?.id,
        conditionSlug,
      });

      updatePriceRecommendation(priceRecommendation);
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="d-flex fx-justify-center ptb-8">
        <RCLoadingBars />
      </div>
    );
  }

  return (
    <div className={classNames('sell-form-pricing-guidance', { 'sell-form-pricing-guidance--compact': autofillPrice })}>
      {hasNoEstimate ? (
        <div className="sell-form-pricing-guidance__no-estimate">
          <RCText htmlTag="p">
            {I18n.t('discovery.sellForm.pricingSection.pricingGuidance.competitivePriceSellsFaster')}
          </RCText>
        </div>
      ) : (
        <div>
          <div className="sell-form-pricing-guidance__header">
            <RCText.Title size="500" htmlTag="h2">
              {I18n.t('discovery.sellForm.pricingSection.pricingGuidance.title')}
            </RCText.Title>
          </div>

          <div className="sell-form-pricing-guidance__body">
            { autofillPrice &&
              <div className="sell-form-pricing-guidance__price-guide-badge">
                <RCText.Eyebrow >
                  <I18N text="discovery.sellForm.pricingSection.pricingGuidance.reverbPriceGuide" />
                </RCText.Eyebrow>
              </div>
            }
            <div className="sell-form-pricing-guidance__body__competitive-pricing">
              <div className="sell-form-pricing-guidance__body__competitive-pricing__price">
                <img
                  src={photoSrc}
                  alt={I18n.t('discovery.sellForm.pricingSection.pricingGuidance.graphIcon')}
                />

                <div>
                  <RCText.Utility size="400" weight="medium">
                    {I18n.t(`discovery.sellForm.pricingSection.pricingGuidance.${autofillPrice ? 'likelyToSell' : 'competitivePrice'}`)}
                  </RCText.Utility>

                  <PriceDisplay
                    amountCents={priceMiddle.amountCents}
                    display={priceMiddle.display}
                  />
                </div>
              </div>

              <div className="sell-form-pricing-guidance__body__competitive-pricing__description">
                {!autofillPrice &&
                <I18N
                  html
                  text="discovery.sellForm.pricingSection.pricingGuidance.description"
                  args={{
                    modelName: canonicalProduct.title,
                    conditionName: I18n.t(`discovery.conditions.${conditionSlug}`),
                  }}
                />}
              </div>
            </div>

            <div className="sell-form-pricing-guidance__body__estimated-price-range-section">
              <RCText.Utility size="400" weight="medium" htmlTag="span">
                {I18n.t('discovery.sellForm.pricingSection.pricingGuidance.estimatedPriceRange')}
              </RCText.Utility>

              <PriceDisplayRange
                moneyLow={priceLow}
                moneyHigh={priceHigh}
                displayConfig={{ precision: 0 }}
              />

              <div>
                <SellFormPricingGuidanceModal
                  canonicalProductIds={[canonicalProduct.id]}
                  countryCode={countryCode}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SELL_FORM_PRICE_RECOMMENDATIONS_QUERY = gql(`
    query DataServices_SellForm__PricingRecommendations(
      $priceRecommendationQueries: [Input_reverb_pricing_PriceRecommendationQuery]
      $loggedOut: Boolean!
    ) {
      priceRecommendations(input: {priceRecommendationQueries: $priceRecommendationQueries}) {
        priceRecommendations {
          priceLow {
            amountCents
            amount
            currency
          }
          priceMiddle {
            amountCents
            amount
            currency
          }
          priceHigh {
            amountCents
            amount
            currency
          }
        }
      }
      me @skip(if: $loggedOut) {
        _id
        uuid
        shop {
          _id
          currency
        }
      }
      exchangeRates(input: { inverseReverseRates: true }) {
        rates {
          from
          to
          rate
        }
      }
    }
  `);
